
/*
 * VNCcontact+ : A new level of contact management
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import * as fromFrequntlyContacts from "../actions/frequently-contacts.action";
import { createEntityAdapter, EntityAdapter, EntityState } from "@ngrx/entity";
import { Action } from "../../../actions";
import { Contact } from "src/app/common/models";

export interface ContactFrequntlyState extends EntityState<Contact> {
  isLoaded: boolean;
  isLoading: boolean;

  isNextPageLoading: boolean;
  isNextPageLoaded: boolean;
  currentOffset: number;
  isMoreContacts: boolean;

  selectedContactsIds: number[];
  isSearchMode: boolean;
  queryParams: any;
}

export const frequentlyContactAdapter: EntityAdapter<Contact> = createEntityAdapter<Contact>({
  selectId: (contact: Contact) => contact.id
});

export function sortByFirstCharacter(contact1: Contact, contact2: Contact): number {
  return contact1.fullName.charCodeAt(0) - contact2.fullName.charCodeAt(0);
}

export const initialState: ContactFrequntlyState = frequentlyContactAdapter.getInitialState({
  isLoading: false,
  isLoaded: false,

  isNextPageLoading: false,
  isNextPageLoaded: false,
  currentOffset: 0,
  isMoreContacts: false,

  selectedContactsIds: [],
  sortBy: null,
  isSearchMode: false,
  queryParams : null
});

export function frequentlyContactReducer(
  state = initialState,
  action: Action
): ContactFrequntlyState {
  switch (action.type) {
    case fromFrequntlyContacts.FrequntlyContactsAcTypes.LOAD_FREQUENTLY_CONTACTS: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.LOAD_FREQUENTLY_CONTACTS_SUCCESS: {
      return frequentlyContactAdapter.addAll(action.payload.contact, {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.LOAD_FREQUENTLY_CONTACTS_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.NEXT_LOAD_FREQUENTLY_CONTACTS: {
      return {
        ...state,
        isNextPageLoading: true,
        isLoading: true
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.NEXT_LOAD_FREQUENTLY_CONTACTS_SUCCESS: {
      const newState = frequentlyContactAdapter.addMany(action.payload.contacts, {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: true,
        isLoading: false,
        isLoaded: true,
        currentOffset: action.payload.currOffset,
        isMoreContacts: action.payload.isMoreContacts
      });

      const changes = action.payload.contacts.map(contact => {
        return {id: contact.id, changes: contact };
      });
      return frequentlyContactAdapter.updateMany(changes, newState);
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.EMPTY_FREQUENTLY_CONTACTS_SUCCESS: {
      const newState = frequentlyContactAdapter.removeAll( {
        ...state,
        isLoading: false,
        isLoaded: true,
        currentOffset: 0,
        isMoreContacts: false
      });
      return newState;
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.NEXT_LOAD_FREQUENTLY_CONTACTS_FAIL: {
      return {
        ...state,
        isNextPageLoading: false,
        isNextPageLoaded: false,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.UPDATE_FREQUENTLY_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.UPDATE_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.updateOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.UPDATE_FREQUENTLY_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.CREATE_FREQUENTLY_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.CREATE_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.addOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.CREATE_FREQUENTLY_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.REMOVE_FREQUENTLY_CONTACT: {
      return {
        ...state,
        isLoading: true,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.REMOVE_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.removeOne(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.REMOVE_MANY_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.removeMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.REMOVE_FREQUENTLY_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.RESTORE_FREQUENTLY_CONTACT: {
      return {
        ...state,
        isLoading: false,
        isLoaded: true,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.RESTORE_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.addMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.RESTORE_FREQUENTLY_CONTACT_FAIL: {
      return {
        ...state,
        isLoading: false,
        isLoaded: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.SEARCH_TOGGLE: {
      return  {
        ...state,
        isSearchMode: action.payload,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.START_FREQUENTLY_LOADING: {
      return {
        ...state,
        isLoading: true
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.STOP_FREQUENTLY_LOADING: {
      return {
        ...state,
        isLoading: false,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.STORE_FREQUENTLY_SEARCHPARAMS: {
      return {
        ...state,
        queryParams: action.payload,
      };
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.RESTORE_SAVED_STATE: {
      const savedState = action.payload.contactState;
      return savedState ? { ...state, ...savedState } : state;
    }

    case fromFrequntlyContacts.FrequntlyContactsAcTypes.UPDATE_MANY_FREQUENTLY_CONTACT_SUCCESS: {
      return frequentlyContactAdapter.updateMany(action.payload, {
        ...state,
        isLoading: false,
        isLoaded: true,
      });
    }

  }
  return state;
}

export const _getIsSearchMode = (state: ContactFrequntlyState) => state.isSearchMode;
export const _getSelectedFrequntlyContactsIds = (state: ContactFrequntlyState) => state.selectedContactsIds;
export const _getIsFrequntlyContactsLoading = (state: ContactFrequntlyState) => state.isLoading;
export const _getIsFrequntlyContactsLoaded = (state: ContactFrequntlyState) => state.isLoaded;
export const _getCurrentFrequntlyOffset = (state: ContactFrequntlyState) => state.currentOffset;
export const _getIsFrequntlyNextPageLoading = (state: ContactFrequntlyState) => state.isNextPageLoading;
export const _getIsFrequntlyNextPageLoaded = (state: ContactFrequntlyState) => state.isNextPageLoaded;
export const _getFrequntlySearchParams = (state: ContactFrequntlyState) => state.queryParams;
export const _getIsFrequntlyMoreContacts = (state: ContactFrequntlyState) => state.isMoreContacts;
